/**
 * The geometry under every chart, as plain functions.
 *
 * ── WHY THIS FILE HAS NO REACT AND NO MONEY IN IT ──
 * Scales, ticks, stack offsets and arcs are the parts of a chart that go
 * wrong quietly - a bar drawn from the wrong baseline, a ring that closes a
 * degree short, a tick at 1,234.56 - and they are the parts a test can pin
 * without a browser. So they take INTEGERS (minor units, already parsed by
 * `@fint/money` in the component) and return pixels and radians. Nothing here
 * is money arithmetic: a share is a ratio, a pixel is a pixel. Keeping
 * `@fint/money` out is also what lets this run under Node's test runner -
 * its ESM build has extensionless imports, which Vite resolves and Node does
 * not, the same reason `overview-rows.ts` keeps its sums in the component.
 */

// ── the value axis ────────────────────────────────────────────────────────

/** A straight map from a value range to a pixel range. Clamps nothing: a point outside the domain is the caller's mistake to see. */
export function linearScale(domain: [number, number], range: [number, number]): (value: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  if (span === 0) return () => r0;
  return (value) => r0 + ((value - d0) / span) * (r1 - r0);
}

/**
 * A tick step that reads as a round number: 1, 2, 2.5, 5 × a power of ten.
 * Over `count` intervals across `span`, the smallest such step that gives
 * `count` or fewer intervals.
 */
export function niceStep(span: number, count: number): number {
  if (span <= 0 || count <= 0) return 1;
  const rough = span / count;
  const power = 10 ** Math.floor(Math.log10(rough));
  for (const multiple of [1, 2, 2.5, 5, 10]) {
    const step = multiple * power;
    if (step >= rough) return step;
  }
  return 10 * power;
}

/**
 * Round ticks covering [min, max], ALWAYS including zero.
 *
 * Zero is the baseline every bar grows from and the line a loss crosses, so
 * an axis that runs 1,200 to 9,800 - as an autoscaled one would - hides the
 * one reference a money chart cannot do without. A flat domain (every value
 * zero) is a single tick at zero; the caller draws an empty state, not an
 * axis, in that case, but the function still answers.
 */
export function niceTicks(min: number, max: number, count = 4): number[] {
  const lo = Math.min(0, min);
  const hi = Math.max(0, max);
  if (lo === 0 && hi === 0) return [0];
  const step = niceStep(hi - lo, count);
  const first = Math.floor(lo / step) * step;
  const last = Math.ceil(hi / step) * step;
  const out: number[] = [];
  // Multiply rather than accumulate, so 0.1-sized steps never drift into
  // 0.30000000000000004 and the ticks stay on round numbers.
  for (let i = 0; first + i * step <= last + step / 2; i += 1) out.push(round(first + i * step));
  return out;
}

const round = (value: number) => Math.round(value * 1e6) / 1e6;

/** The domain those ticks span: the first and last of them. */
export function niceDomain(min: number, max: number, count = 4): [number, number] {
  const ticks = niceTicks(min, max, count);
  const first = ticks[0] ?? 0;
  const last = ticks[ticks.length - 1] ?? 0;
  return first === last ? [first, first + 1] : [first, last];
}

// ── the category axis ─────────────────────────────────────────────────────

export interface Band {
  /** Left edge of slot `i`'s bar. */
  x: (i: number) => number;
  /** Width of one bar. */
  band: number;
  /** Distance from one slot to the next. */
  step: number;
  /** The slot under a pixel, clamped to the ends: a pointer past the last bar means the last bar. */
  index: (px: number) => number;
  count: number;
}

/**
 * `count` equal slots across [start, end], each holding a bar and some air.
 *
 * `paddingInner` is the air between slots as a fraction of a step;
 * `paddingOuter` is the air at each end in the same unit. Zero slots is a
 * band nobody can index - `index` answers -1 and `x` answers `start`.
 */
export function bandScale(count: number, start: number, end: number, paddingInner = 0.35, paddingOuter = 0.2): Band {
  if (count <= 0) {
    return { x: () => start, band: 0, step: 0, index: () => -1, count: 0 };
  }
  const width = end - start;
  const step = width / (count - paddingInner + 2 * paddingOuter);
  const band = step * (1 - paddingInner);
  const x = (i: number) => start + step * (paddingOuter + i);
  const index = (px: number) => {
    const raw = Math.floor((px - start - step * paddingOuter + (step - band) / 2) / step);
    return Math.max(0, Math.min(count - 1, raw));
  };
  return { x, band, step, index, count };
}

// ── stacking ──────────────────────────────────────────────────────────────

export interface Segment {
  /** The value the segment starts at - the running total before it. */
  start: number;
  /** The value it ends at. `end - start` is the segment's own value. */
  end: number;
}

/**
 * Where each value sits in a stack: positives pile up from zero, negatives
 * pile down from zero, so a credit note in one book never eats into another
 * book's bar. A zero value is a zero-height segment at the current top, so
 * indices stay aligned with the input.
 */
export function stackSegments(values: number[]): Segment[] {
  let up = 0;
  let down = 0;
  return values.map((value) => {
    if (value >= 0) {
      const start = up;
      up += value;
      return { start, end: up };
    }
    const start = down;
    down += value;
    return { start, end: down };
  });
}

/** The top and bottom of a stack - what the axis has to reach. */
export function stackExtent(values: number[]): [number, number] {
  const segments = stackSegments(values);
  let min = 0;
  let max = 0;
  for (const segment of segments) {
    min = Math.min(min, segment.end);
    max = Math.max(max, segment.end);
  }
  return [min, max];
}

// ── the ring ──────────────────────────────────────────────────────────────

export interface Arc {
  /** Radians, clockwise from twelve o'clock. */
  start: number;
  end: number;
  share: number;
}

const TAU = Math.PI * 2;

/**
 * The angles of a ring's slices, clockwise from twelve o'clock.
 *
 * Values at or below zero get a zero-width arc rather than being dropped,
 * so the result lines up with the input and the caller can still key a
 * legend row to its (empty) slice. A total of zero is an empty ring - the
 * caller draws an empty state - and one positive value is the whole circle,
 * which `arcPath` knows how to draw.
 */
export function donutArcs(values: number[]): Arc[] {
  const total = values.reduce((sum, value) => sum + Math.max(0, value), 0);
  if (total <= 0) return [];
  let angle = 0;
  return values.map((value) => {
    const share = Math.max(0, value) / total;
    const start = angle;
    angle += share * TAU;
    return { start, end: angle, share };
  });
}

/** A point on a circle, with the angle measured clockwise from twelve o'clock as `donutArcs` does. */
export function polar(cx: number, cy: number, radius: number, angle: number): { x: number; y: number } {
  return { x: cx + radius * Math.sin(angle), y: cy - radius * Math.cos(angle) };
}

const fixed = (n: number) => Number(n.toFixed(3));

/**
 * The SVG path of one ring segment between two radii.
 *
 * ── THE FULL CIRCLE ──
 * An SVG arc from a point back to the same point draws nothing at all, so a
 * ring with one slice - one category, a hundred percent - rendered as an
 * empty ring. That is the case this function is careful about: a segment
 * that spans the whole turn (or within a hair of it) is drawn as two half
 * arcs, which is what a circle is. A zero-width segment is an empty path,
 * so a legend row's slice can exist without painting a stray line.
 */
export function arcPath(cx: number, cy: number, outer: number, inner: number, start: number, end: number): string {
  const sweep = end - start;
  if (sweep <= 1e-9) return '';
  if (sweep >= TAU - 1e-6) {
    const top = polar(cx, cy, outer, 0);
    const bottom = polar(cx, cy, outer, Math.PI);
    const innerTop = polar(cx, cy, inner, 0);
    const innerBottom = polar(cx, cy, inner, Math.PI);
    return [
      `M ${fixed(top.x)} ${fixed(top.y)}`,
      `A ${outer} ${outer} 0 1 1 ${fixed(bottom.x)} ${fixed(bottom.y)}`,
      `A ${outer} ${outer} 0 1 1 ${fixed(top.x)} ${fixed(top.y)}`,
      `M ${fixed(innerTop.x)} ${fixed(innerTop.y)}`,
      `A ${inner} ${inner} 0 1 0 ${fixed(innerBottom.x)} ${fixed(innerBottom.y)}`,
      `A ${inner} ${inner} 0 1 0 ${fixed(innerTop.x)} ${fixed(innerTop.y)}`,
      'Z',
    ].join(' ');
  }
  const large = sweep > Math.PI ? 1 : 0;
  const o0 = polar(cx, cy, outer, start);
  const o1 = polar(cx, cy, outer, end);
  const i0 = polar(cx, cy, inner, start);
  const i1 = polar(cx, cy, inner, end);
  return [
    `M ${fixed(o0.x)} ${fixed(o0.y)}`,
    `A ${outer} ${outer} 0 ${large} 1 ${fixed(o1.x)} ${fixed(o1.y)}`,
    `L ${fixed(i1.x)} ${fixed(i1.y)}`,
    `A ${inner} ${inner} 0 ${large} 0 ${fixed(i0.x)} ${fixed(i0.y)}`,
    'Z',
  ].join(' ');
}

// ── lines ─────────────────────────────────────────────────────────────────

export interface Point {
  x: number;
  y: number;
}

/** A polyline through the points, lifting the pen over a gap (`null`). */
export function linePath(points: Array<Point | null>): string {
  let out = '';
  let pen = false;
  for (const point of points) {
    if (!point) {
      pen = false;
      continue;
    }
    out += `${pen ? ' L' : (out ? ' M' : 'M')} ${fixed(point.x)} ${fixed(point.y)}`;
    pen = true;
  }
  return out;
}

/** The same line closed down to a baseline, for the wash under it. Gaps split it into separate washes. */
export function areaPath(points: Array<Point | null>, baselineY: number): string {
  const runs: Point[][] = [];
  let run: Point[] = [];
  for (const point of points) {
    if (point) {
      run.push(point);
    } else if (run.length > 0) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length > 0) runs.push(run);
  return runs
    .map((segment) => {
      const first = segment[0];
      const last = segment[segment.length - 1];
      if (!first || !last) return '';
      return `${linePath(segment)} L ${fixed(last.x)} ${fixed(baselineY)} L ${fixed(first.x)} ${fixed(baselineY)} Z`;
    })
    .join(' ');
}

// ── labels ────────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/**
 * `2026-04` as a person reads it. `short` is the axis - "Apr", or "Apr 26"
 * when the run of months crosses a year and the year is the thing that
 * changed; `long` is the tooltip and the table - "April 2026".
 */
export function monthLabel(ym: string, style: 'short' | 'long' = 'long', withYear = style === 'long'): string {
  const [year, month] = ym.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) return ym;
  if (style === 'long') return `${MONTHS_LONG[month - 1]} ${year}`;
  return withYear ? `${MONTHS[month - 1]} ${String(year).slice(2)}` : MONTHS[month - 1] ?? ym;
}

/** Whether a run of months needs the year on its short labels: only when it spans more than one. */
export function spansYears(months: string[]): boolean {
  const years = new Set(months.map((ym) => ym.slice(0, 4)));
  return years.size > 1;
}

/**
 * An axis tick, short: 1.5L, 20K, 2 Cr - or 1.5M, 20K, 2B outside India.
 *
 * Takes MAJOR units (the component divides by the currency's exponent) and
 * answers text; no rounding of money happens here because a tick is a
 * position, not an amount. The minus is U+2212, as the rest of the app signs
 * its figures.
 */
export function compactAmount(major: number, indian: boolean): string {
  const sign = major < 0 ? '−' : '';
  const abs = Math.abs(major);
  const trim = (value: number) => {
    const text = value.toFixed(1);
    return text.endsWith('.0') ? text.slice(0, -2) : text;
  };
  if (indian) {
    if (abs >= 1e7) return `${sign}${trim(abs / 1e7)} Cr`;
    if (abs >= 1e5) return `${sign}${trim(abs / 1e5)} L`;
    if (abs >= 1e3) return `${sign}${trim(abs / 1e3)}K`;
  } else {
    if (abs >= 1e9) return `${sign}${trim(abs / 1e9)}B`;
    if (abs >= 1e6) return `${sign}${trim(abs / 1e6)}M`;
    if (abs >= 1e3) return `${sign}${trim(abs / 1e3)}K`;
  }
  return `${sign}${trim(abs)}`;
}

/** The nearest slot to a pixel, or -1 when there are none. A thin wrapper so callers read as "nearest month". */
export function nearestSlot(band: Band, px: number): number {
  return band.count === 0 ? -1 : band.index(px);
}
